<template>
    <div class="section">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link to="/admin">Admin Settings</router-link></li>
          <li class="is-active"><router-link to="/admin/data-mapping" aria-current="page">Data Mapping</router-link></li>
        </ul>
      </nav>
      <div class="container">
        <h1 class="title">Data Mapping</h1>
  
        <!-- Tabs for Existing Mappings -->
        <div class="tabs is-toggle">
          <ul>
            <li :class="{ 'is-active': activeTab === 'lookup' }" @click="activeTab = 'lookup'">
              <a>Lookup Mappings</a>
            </li>
            <li :class="{ 'is-active': activeTab === 'hardcode' }" @click="activeTab = 'hardcode'">
              <a>Hardcode Mappings</a>
            </li>
          </ul>
        </div>
  
        <!-- Lookup Mappings Table -->
        <div v-if="activeTab === 'lookup'" class="box">
          <h2 class="subtitle">Existing Lookup Mappings</h2>
          <table class="table is-fullwidth">
            <thead>
              <tr>
                <th>Field</th>
                <th>Lookup Key</th>
                <th>Mapped Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mapping in lookupMappings" :key="mapping.id">
                <td>{{ mapping.field }}</td>
                <td>{{ mapping.lookup_key }}</td>
                <td>{{ mapping.lookup_value }}</td>
                <td>
                  <button class="button is-info is-small" @click="openModal(mapping)">Edit</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Hardcode Mappings Table -->
        <div v-if="activeTab === 'hardcode'" class="box">
          <h2 class="subtitle">Existing Hardcode Mappings</h2>
          <table class="table is-fullwidth">
            <thead>
              <tr>
                <th>Field</th>
                <th>Hardcoded Value</th>
                <th>Overwrite</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mapping in hardcodeMappings" :key="mapping.id">
                <td>{{ mapping.field }}</td>
                <td>{{ mapping.hardcode_value }}</td>
                <td>{{ mapping.overwrite ? 'Yes' : 'No' }}</td>
                <td>
                  <button class="button is-info is-small" @click="openModal(mapping)">Edit</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Add/Edit Mapping Modal -->
        <div class="modal" :class="{ 'is-active': isModalActive }">
          <div class="modal-background" @click="closeModal"></div>
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">{{ isEditing ? 'Edit Mapping' : 'Add Mapping' }}</p>
              <button class="delete" aria-label="close" @click="closeModal"></button>
            </header>
            <section class="modal-card-body">
              <form @submit.prevent="isEditing ? updateMapping() : addMapping()">
                <div class="field">
                  <label class="label">Field</label>
                  <div class="control">
                    <select v-model="currentMapping.field" class="input" :disabled="isEditing">
                      <option v-for="field in availableFields" :key="field.value" :value="field.value">
                        {{ field.label }}
                      </option>
                    </select>
                  </div>
                </div>
  
                <!-- Tabs in Modal for Adding/Edit -->
                <div class="tabs is-toggle">
                  <ul>
                    <li :class="{ 'is-active': activeModalTab === 'lookup' }" @click="activeModalTab = 'lookup'">
                      <a>Lookup</a>
                    </li>
                    <li :class="{ 'is-active': activeModalTab === 'hardcode' }" @click="activeModalTab = 'hardcode'">
                      <a>Hardcode</a>
                    </li>
                  </ul>
                </div>
  
                <!-- Lookup Fields -->
                <div v-if="activeModalTab === 'lookup'">
                  <div class="field">
                    <label class="label">Lookup Key</label>
                    <div class="control">
                      <input type="text" v-model="currentMapping.lookup_key" class="input" placeholder="Enter lookup key">
                    </div>
                  </div>
                  <div class="field">
                    <label class="label">Mapped Value</label>
                    <div class="control">
                      <input type="text" v-model="currentMapping.lookup_value" class="input" placeholder="Enter mapped value">
                    </div>
                  </div>
                </div>
  
                <!-- Hardcode Fields -->
                <div v-if="activeModalTab === 'hardcode'">
                  <div class="field">
                    <label class="label">Hardcoded Value</label>
                    <div class="control">
                      <input type="text" v-model="currentMapping.hardcode_value" class="input" placeholder="Enter hardcoded value">
                    </div>
                  </div>
                  <div class="field">
                    <label class="checkbox">
                      <input type="checkbox" v-model="currentMapping.overwrite">
                      Overwrite Existing Value
                    </label>
                  </div>
                </div>
              </form>
            </section>
            <footer class="modal-card-foot">
              <div class="buttons">
                <button class="button is-primary" @click="isEditing ? updateMapping() : addMapping()">
                  {{ isEditing ? 'Save Changes' : 'Add Mapping' }}
                </button>
                <button class="button" @click="closeModal">Cancel</button>
                <button
                  v-if="isEditing"
                  class="button is-danger"
                  @click="confirmDeleteMapping(currentMapping.id)"
                >
                  Delete Mapping
                </button>
              </div>
            </footer>
          </div>
        </div>
  
        <!-- Add Mapping Button -->
        <div class="control mt-5">
          <button class="button is-primary" @click="openModal()">Add Data Mapping</button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from "axios";
  import { toast } from "bulma-toast";
  
  export default {
    name: "DataMapping",
    data() {
      return {
        availableFields: [],
        mappings: [],
        lookupMappings: [],
        hardcodeMappings: [],
        isModalActive: false,
        isEditing: false,
        activeTab: "lookup", // Active tab for table
        activeModalTab: "lookup", // Active tab in modal
        currentMapping: {
          id: null,
          field: "",
          mapping_type: "",
          lookup_key: "",
          lookup_value: "",
          hardcode_value: "",
          overwrite: false,
        },
      };
    },
    methods: {
      async fetchFields() {
        try {
          const response = await axios.get("/api/v1/inputvalidation/invoice-fields/");
          this.availableFields = response.data;
        } catch (error) {
          console.error("Error fetching fields:", error);
          toast({
            message: "Failed to load fields.",
            type: "is-danger",
            duration: 3000,
            position: "top-right",
          });
        }
      },
      async fetchMappings() {
        try {
          const response = await axios.get("/api/v1/inputvalidation/data-mapping/");
          this.mappings = response.data.results || [];
          this.lookupMappings = this.mappings.filter((mapping) => mapping.mapping_type === "lookup");
          this.hardcodeMappings = this.mappings.filter((mapping) => mapping.mapping_type === "hardcode");
        } catch (error) {
          console.error("Error fetching mappings:", error);
          toast({
            message: "Failed to load mappings.",
            type: "is-danger",
            duration: 3000,
            position: "top-right",
          });
        }
      },
      async addMapping() {
        this.currentMapping.mapping_type = this.activeModalTab;
        try {
          await axios.post("/api/v1/inputvalidation/data-mapping/", this.currentMapping);
          toast({
            message: "Mapping added successfully.",
            type: "is-success",
            duration: 3000,
            position: "top-right",
          });
          this.closeModal();
          this.fetchMappings();
        } catch (error) {
          console.error("Error adding mapping:", error);
          toast({
            message: "Failed to add mapping.",
            type: "is-danger",
            duration: 3000,
            position: "top-right",
          });
        }
      },
      async updateMapping() {
        this.currentMapping.mapping_type = this.activeModalTab;
        try {
          await axios.put(`/api/v1/inputvalidation/data-mapping/${this.currentMapping.id}/`, this.currentMapping);
          toast({
            message: "Mapping updated successfully.",
            type: "is-success",
            duration: 3000,
            position: "top-right",
          });
          this.closeModal();
          this.fetchMappings();
        } catch (error) {
          console.error("Error updating mapping:", error);
          toast({
            message: "Failed to update mapping.",
            type: "is-danger",
            duration: 3000,
            position: "top-right",
          });
        }
      },
      async confirmDeleteMapping(id) {
        const confirmDelete = window.confirm("Are you sure you want to delete this mapping?");
        if (!confirmDelete) return;
  
        try {
          await axios.delete(`/api/v1/inputvalidation/data-mapping/${id}/`);
          toast({
            message: "Mapping deleted successfully.",
            type: "is-success",
            duration: 3000,
            position: "top-right",
          });
          this.closeModal();
          this.fetchMappings();
        } catch (error) {
          console.error("Error deleting mapping:", error);
          toast({
            message: "Failed to delete mapping.",
            type: "is-danger",
            duration: 3000,
            position: "top-right",
          });
        }
      },
      openModal(mapping = null) {
        this.isEditing = !!mapping;
        this.currentMapping = mapping
          ? { ...mapping }
          : { id: null, field: "", mapping_type: "", lookup_key: "", lookup_value: "", hardcode_value: "", overwrite: false };
        this.activeModalTab = mapping?.mapping_type || "lookup";
        this.isModalActive = true;
      },
      closeModal() {
        this.isModalActive = false;
      },
    },
    mounted() {
      this.fetchFields();
      this.fetchMappings();
    },
  };
  </script>
  
  <style scoped>
  .table {
    margin-top: 20px;
  }
  .modal {
    z-index: 1000;
  }
  .tabs {
    margin-bottom: 20px;
  }
  </style>
  